import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { Server, ServerResponse } from '../../utils/Server';
import { FormatDate, FormatMoney, sanitizeHtml } from '../../utils/Tools';
import Icon, { IconType } from '../Icon/Icon';
import Modal from '../Modal/Modal';
import { IInvoice, IInvoiceInfo, IInvoicesInfo } from './OrganizationTypes';
import { useEffect, useState } from 'react';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import Payment from '../Store/Payment';
import StripPayment, { istripForm } from './stripePayment';
import InvoiceEditor from './InvoiceEditor';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

interface IInvoiceProps {
  ShowAchInfo: boolean;
  ShowCheckInfo: boolean;
}

interface IPayInvoice {
  Invoice: IInvoice;
  Message: string;
}

interface iform {
  invoice?: string;
  paidOn?: string;
  notes?: string;
}

interface IDeclineForm {
  declineType: 'NotRenewing' | 'NotNeeded' | '';
  reason: string;
  confirmed: boolean;
}

const declineTypeLabel = (type?: string) => {
  if (type === 'NotRenewing') return 'Not renewing';
  if (type === 'NotNeeded') return 'Invoice not needed';
  return type || 'Declined';
};

const Invoice = (props: IInvoiceProps) => {
  const { invoiceId } = useParams();
  const [info, setInfo] = useState<IInvoiceInfo>();
  const [showPayNow, setShowPayNow] = useState<boolean>();
  const [showPayNowManual, setShowPayNowManual] = useState<boolean>();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>();
  const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
  const [declineBusy, setDeclineBusy] = useState<boolean>(false);
  const [form, setForm] = useState<iform>({});
  const [declineForm, setDeclineForm] = useState<IDeclineForm>({
    declineType: '',
    reason: '',
    confirmed: false,
  });

  useEffect(() => {
    init();
  }, []);

  const init = () => {
    store.server.getApi<ServerResponse<IInvoiceInfo>>(`../OrganizationData/Invoice/${invoiceId}`).then((x) => {
      if (x.Success) {
        setInfo(x.Value);
        initStripe(x.Value.StripeKey);
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  const initStripe = (key: string) => {
    setStripePromise(loadStripe(key));
  };

  const buildInvoiceBlip = (x: IInvoice, i: number) => {
    let link = StoreX.BuildUrl(`../app/organization/invoice/${x.PublicId}`);
    return (
      <div className={`flex-between invoice ${x.PaidAtUtc ? 'paid' : ''}`} key={`i-k-${i}`}>
        <div>
          <div>
            <div>
              <img width="250" src='https://zfairs.com/file/illuminatingSoftwareLogo.png' alt='Illuminating Software Logo' />
            </div>

            <div className="soft">send payment to:</div>
            <div className="strong">Illuminating Software llc</div>
            <div className="flex-between">
              <div>
                <div>3688 S Rowan Cove</div>
                <div>West Valley City, UT 84128</div>
              </div>
              <div></div>
            </div>
          </div>
        </div>

        <div className="text-right">
          <div className="no-print">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                window.print();
              }}>
              <Icon type={IconType.print} /> Print
            </button>
          </div>
          <div className="large">
            {x.Type} # {x.Id}
          </div>
          <div>
            {x.Type} Date: {FormatDate(x.CreatedAtUtc, 'date')}
          </div>
          {x.DueAtUtc && <div>Due: {FormatDate(x.DueAtUtc, 'date')}</div>}
          {x.SubscriptionValidThruUtc && (
            <div>
              Subscription through {FormatDate(x.SubscriptionValidThruUtc, 'date', true)}
              {!x.PaidAtUtc && ' once paid'}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handlePayNow = async (stripe: Stripe, elements: StripeElements, form: istripForm) => {
    //do I have the info I want?
    let errors: string[] = [];
    if (!form.firstName || form.firstName?.length === 0) errors.push(`First name required`);
    if (!form.lastName || form.lastName?.length === 0) errors.push(`Last name required`);

    if (errors.length > 0) {
      alert(errors.join(', '));
      return;
    }

    if (!stripe || !elements) {
      return;
    }
    let thisElement = elements.getElement(CardElement);
    if (thisElement === null) return;

    stripe.createToken(thisElement).then((x) => {
      if (x.error) {
        console.log(x.error);
        alert(x.error.message);
      } else {
        let process = {
          ...form,
          year: x?.token?.card?.exp_year,
          month: x.token.card?.exp_month,
          last4: x.token.card?.last4,
          zip: x.token.card?.address_zip,
          token: x.token.id,
          orderId: info?.Invoice.PublicId,
        };
        ProcessPayment(process);
      }
    });
  };

  const ProcessPayment = (payload) => {
    let url = `../OrganizationData/PayInvoice`;

    store.server.postApi<ServerResponse<IPayInvoice>>(url, payload).then((x) => {
      if (x.Success) {
        if (info) setInfo({ ...info, Invoice: x.Value.Invoice });
        if (x.Value.Invoice.PaidAtUtc) {
          setShowPayNow(false);
          toast.success(`Payment Received!`);
        } else {
          toast.warning(`Payment Failed: ${x.Value.Message}`, { autoClose: false });
        }
      } else {
        toast.error(`${x.Value?.Message ?? ''} ${x.Message}`, { autoClose: false });
      }
    });
  };

  const recordManualPayment = () => {
    let request = { ...form, Invoice: info?.Invoice.PublicId };

    if (!window.confirm(`Are you sure you want to record this manual payment?`)) {
      return;
    }

    let url = `../OrganizationData/MarkInvoicePaid`;

    store.server.postApi<ServerResponse<IPayInvoice>>(url, request).then((x) => {
      if (x.Success) {
        if (info) setInfo({ ...info, Invoice: x.Value.Invoice });
        if (x.Value.Invoice.PaidAtUtc) {
          setShowPayNow(false);
          setShowPayNowManual(false);
          toast.success(`Payment Recorded!`);
        } else {
          toast.warning(`Payment Failed: ${x.Value.Message}`, { autoClose: false });
        }
      } else {
        toast.error(`${x.Value?.Message ?? ''} ${x.Message}`, { autoClose: false });
      }
    });
  };

  const openDeclineModal = () => {
    setDeclineForm({ declineType: '', reason: '', confirmed: false });
    setShowDeclineModal(true);
  };

  const declineInvoice = () => {
    if (!info?.Invoice?.PublicId) return;

    if (!declineForm.declineType) {
      toast.warning('Please choose why you are declining this invoice.');
      return;
    }
    if (!declineForm.reason || declineForm.reason.trim().length < 3) {
      toast.warning('Please provide a brief reason.');
      return;
    }
    if (!declineForm.confirmed) {
      toast.warning('Please confirm you understand before declining.');
      return;
    }

    setDeclineBusy(true);
    store.server
      .postApi<ServerResponse<IInvoice>>(`../OrganizationData/DeclineInvoice`, {
        Invoice: info.Invoice.PublicId,
        DeclineType: declineForm.declineType,
        Reason: declineForm.reason.trim(),
      })
      .then((x) => {
        if (x.Success) {
          setInfo({ ...info, Invoice: x.Value });
          setShowDeclineModal(false);
          toast.dark('Invoice declined. Support has been notified.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setDeclineBusy(false));
  };

  const undoDecline = () => {
    if (!info?.Invoice?.PublicId) return;
    if (
      !window.confirm(
        'Undo decline? This invoice will become open again (payment due / notices may resume).'
      )
    ) {
      return;
    }

    setDeclineBusy(true);
    store.server
      .postApi<ServerResponse<IInvoice>>(`../OrganizationData/UndoDeclineInvoice`, {
        invoice: info.Invoice.PublicId,
      })
      .then((x) => {
        if (x.Success) {
          setInfo({ ...info, Invoice: x.Value });
          toast.success('Decline undone. Invoice is open again.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setDeclineBusy(false));
  };

  return (
    <>
      <div className="org-invoice" data-testid="org-invoice">
        {info ? (
          <>
            {buildInvoiceBlip(info.Invoice, -1)}
            <hr />

            {(info?.Invoice?.Items?.length ?? 0) > 0 && (
              <div>
                {info.Invoice.Items?.filter((x) => x.Quantity ?? 0 > 0).map((x, i) => {
                  return (
                    <div className="flex-between invoice-item" key={`iik-${i}`}>
                      <div>
                        <strong>{x.Name} </strong>
                        {x.Name !== x.ItemName && <small>{x.ItemName}</small>}
                        {x.Notes && <div className="invoice-notes" dangerouslySetInnerHTML={{ __html: sanitizeHtml(x.Notes) }}></div>}
                      </div>
                      <div className="text-right amount">
                        {(x.Quantity ?? 0) > 1 && <div>Quantity: {x.Quantity}</div>}
                        {(x.Cost ?? 0) != 0 && <div>{FormatMoney(x.Cost)}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {info?.Invoice.Notes && <div className="headroom main invoice-notes" dangerouslySetInnerHTML={{ __html: sanitizeHtml(info.Invoice.Notes) }}></div>}
            <hr />
            <br />
            <div className="flex-between">
              <div>
                {info && info.Invoice.PaidAtUtc && (
                  <div className="alert alert-success">
                    <h4>Paid via {info.Invoice.Method}</h4>
                    <div>
                      {info.Invoice.Type} Payment at {FormatDate(info.Invoice.PaidAtUtc)}
                      {info.Invoice.PaymentId && <div>Payment Auth: {info.Invoice.PaymentId}</div>}
                    </div>
                    {info.IsOwner && !info.Invoice.RemovedAtUtc && (
                      <div className="headroom no-print">
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={() => {
                            setShowEditModal(true);
                          }}>
                          <Icon type={IconType.edit} /> Edit
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {info && info.Invoice.RemovedAtUtc && (
                  <div className="alert alert-danger">
                    <h4>Removed at {FormatDate(info.Invoice.RemovedAtUtc)}</h4>
                  </div>
                )}

                {info && info.Invoice.DeclinedAtUtc && !info.Invoice.PaidAtUtc && !info.Invoice.RemovedAtUtc && (
                  <div className="alert alert-warning">
                    <h4>Invoice Declined</h4>
                    <div>
                      <div>
                        <strong>{declineTypeLabel(info.Invoice.DeclineType)}</strong>
                        {' · '}
                        {FormatDate(info.Invoice.DeclinedAtUtc)}
                      </div>
                      {info.Invoice.DeclineReason && (
                        <div className="headroom">Reason: {info.Invoice.DeclineReason}</div>
                      )}
                      <div className="headroom soft">
                        This invoice is no longer due. Support has been notified. If you need zFairs access or this was a mistake, you can undo the decline or contact support@zfairs.com.
                      </div>
                      <div className="headroom no-print">
                        <button
                          type="button"
                          className="btn btn-default"
                          disabled={declineBusy}
                          onClick={undoDecline}>
                          <Icon type={IconType.undo} /> Undo Decline
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="text-right">
                {info.Invoice.CardProcessingFee > 0 && <div title="Card Processing Fee">Card Processing Fee: {FormatMoney(info.Invoice.CardProcessingFee)}</div>}
                <div className="large">Total: {FormatMoney(info.Invoice.Amount + (info.Invoice.CardProcessingFee ?? 0))}</div>
                {info.Invoice.PurchaseOrderName && <div className="alert alert-info strong">Purchase Order: {info.Invoice.PurchaseOrderName}</div>}
              </div>
            </div>
            {!info.Invoice.PaidAtUtc && !info.Invoice.RemovedAtUtc && (
              <div className="flex-between text-right no-print">
                <div>
                  {!info.Invoice.DeclinedAtUtc && (
                    <button type="button" className="btn btn-default headroom" onClick={openDeclineModal}>
                      <Icon type={IconType.ban} /> Decline Invoice
                    </button>
                  )}
                </div>
                <div className="btn-group headroom">
                  {!info.Invoice.DeclinedAtUtc && (info.Invoice.Amount ?? 0) > 0 && (info.Invoice.Amount ?? 0) < 10001 && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowPayNow(true);
                      }}>
                      <Icon type={IconType.creditCard} /> Pay Now
                    </button>
                  )}
                  {info.IsOwner && (
                    <>
                      {!info.Invoice.DeclinedAtUtc && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowPayNowManual(true);
                          }}>
                          <Icon type={IconType.creditCard} /> Record Payment
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => {
                          setShowEditModal(true);
                        }}>
                        <Icon type={IconType.edit} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="text-muted headroom-xl text-center">If you require a W9 please email support@zfairs.com and reference this invoice. # {info.Invoice.Id}</div>
            <div className="text-muted headroom tos">
              <h4>Service Basics</h4>
              Unless you have a different Agreement with zFairs or Illuminating Software the following applies: <br />
              <br />
              {/* <a href={'https://www.zfairs.com/Legal/zFairs Service Agreement.pdf'} target={'_blank'}>
                zFairs Service Agreement
              </a> */}
              <ul>
                <li>
                  Data Security
                  <ul>
                    <li>Data is encrypted via transit and at rest.</li>
                    <li>You maintain full ownership of your data, zFairs does not sell or share information.</li>
                    <li>zFairs is actively working to ensure your data is safe, secure, and available</li>
                    <li>All data and services are in the USA, in secure SOC II && PCI compliant data centers.</li>
                  </ul>
                </li>
                <li>
                  Hosting
                  <ul>
                    <li>zFairs is accessible via any modern browser</li>
                    <li>zFairs works to resolve any service interruptions or defects as quickly as possible.</li>
                    <li>zFairs works to ensure its services are always available when you need them.</li>
                    <li>
                      <a href={'https://zfairs.com/Policy/TermsOfUse'} target={'_blank'}>
                        Terms of Service
                      </a>
                    </li>
                  </ul>
                </li>
                <li>
                  Privacy
                  <ul>
                    <li>We do not sell or share information</li>
                    <li>We comply with US FERPA and COPPA laws, and are committed to continue working to always stay in compliance.</li>
                    <li>Upon request we will completely remove data from all active systems and databases. </li>
                    <li>
                      <a href={'https://zfairs.com/Policy/Privacy'} target={'_blank'}>
                        Privacy Policy
                      </a>
                    </li>
                  </ul>
                </li>
                <li>
                  Support
                  <ul>
                    <li>zFairs is committed to ensuring you have everything you need to run your event smoothly, zFairs support can be reached at support@zfairs.com </li>
                  </ul>
                </li>
                <li>
                  Features
                  <ul>
                    <li>Standard zFairs license include the following</li>
                  </ul>
                  <div className="org-invoice-features">
                    <ul>
                      <li>All Base Features</li>
                      <li>In Person & Virtual Tools</li>
                      <li>Online Judging for In person & Virtual</li>
                      <li>Video Conferencing</li>
                      <li>Judging Assignment Tools</li>
                      <li>Awards Management</li>
                      <li>Certificates, & Reports</li>
                    </ul>
                    <ul>
                      <li>Showcase</li>
                      <li>Paperwork & Forms Management</li>
                      <li>Forms Wizard & ISEF Forms Wizard</li>
                      <li>Payment & Credit Card Processing</li>
                      <li>Invoicing & Purchase Orders</li>
                      <li>Project / Entry Advancement</li>
                      <li>Participant & Entry Images</li>
                      <li>Support</li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </>
        ) : (
          <>Loading...</>
        )}
      </div>
      {showPayNow && info && (
        <Modal setModalOpen={setShowPayNow} title={'Pay Now'} size="l">
          <Tabs>
            <TabList>
              <Tab><Icon type={IconType.creditCard} /> Credit Card</Tab>
              {props.ShowCheckInfo && <Tab><Icon type={IconType.check} /> Check</Tab>}
              {props.ShowAchInfo && <Tab><Icon type={IconType.wire} /> Wire / ACH</Tab>}
            </TabList>
            <TabPanel>
              <Elements stripe={stripePromise}>
                <StripPayment
                  Amount={info.Invoice.Amount ?? 0}
                  handlePayNow={handlePayNow}
                  feeAmount={0.75}
                  feePercent={0.037} //.029
                  includeFees={!info.Invoice.WaveCardProcessingFee}
                />
              </Elements>
            </TabPanel>
            {props.ShowCheckInfo && <TabPanel>
              <h3>Pay via Check</h3>
              <p>
                Please make checks payable to illuminating Software and mail to:
              </p>
              <p>
                illuminating Software
                <br />
                3688 S Rowan Cove
                <br />
                West Valley City, UT 84128
              </p>
            </TabPanel>}
            {props.ShowAchInfo && <TabPanel>
              <h3>Pay via Wire or ACH</h3>
              <p>
                Please reach out to support at support@zfairs.com or 800-252-0903 x1 to get wire/ACH instructions.
              </p>
            </TabPanel>}
          </Tabs>
        </Modal>
      )}
      {showPayNowManual && info && (
        <Modal setModalOpen={setShowPayNowManual} title={'Record Payment'} size="l">
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="org-paidOn">
                Paid On
              </label>
              <div className="col-sm-8">
                <input
                  type="date"
                  id="org-paidOn"
                  onChange={(e) => {
                    setForm({ ...form, paidOn: e.target.value });
                  }}
                  value={form.paidOn ?? ''}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="org-notes">
                Public Notes
              </label>
              <div className="col-sm-8">
                <textarea
                  rows={5}
                  id="org-notes"
                  onChange={(e) => {
                    setForm({ ...form, notes: e.target.value });
                  }}
                  value={form.notes ?? ''}
                  className="form-control"
                />
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <button type="button" className="btn btn-secondary" onClick={recordManualPayment}>
                  <Icon type={IconType.payNow} /> Record Payment
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showEditModal && info && (
        <Modal setModalOpen={setShowEditModal} title={'Update Invoice'} size="l">
          <InvoiceEditor
            invoice={info.Invoice}
            isOwner={info.IsOwner}
            onUpdated={(invoice: IInvoice) => {
              setInfo({ ...info, Invoice: invoice });
              if (invoice.RemovedAtUtc) setShowEditModal(false);
            }}
            products={info.Products ?? []}
          />
        </Modal>
      )}

      {showDeclineModal && info && (
        <Modal setModalOpen={setShowDeclineModal} title={'Decline Invoice'} size="m">
          <div className="alert alert-danger">
            <h4>
              <Icon type={IconType.warning} /> Please read before declining
            </h4>
            <ul>
              <li>Declining means you are not paying this invoice and do not need it at this time.</li>
              <li>This does not guarantee continued system access — support will follow up if needed.</li>
              <li>This is not a refund and does not reverse prior payments.</li>
              <li>You can undo this later from this invoice page if you change your mind.</li>
            </ul>
          </div>
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-4">Why are you declining?</label>
              <div className="col-sm-8">
                <div className="radio">
                  <label>
                    <input
                      type="radio"
                      name="declineType"
                      checked={declineForm.declineType === 'NotRenewing'}
                      onChange={() => setDeclineForm({ ...declineForm, declineType: 'NotRenewing' })}
                    />{' '}
                    Not renewing / will not use the system
                  </label>
                </div>
                <div className="radio">
                  <label>
                    <input
                      type="radio"
                      name="declineType"
                      checked={declineForm.declineType === 'NotNeeded'}
                      onChange={() => setDeclineForm({ ...declineForm, declineType: 'NotNeeded' })}
                    />{' '}
                    Invoice not needed (duplicate, error, etc.)
                  </label>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="decline-reason">
                Reason
              </label>
              <div className="col-sm-8">
                <textarea
                  id="decline-reason"
                  rows={4}
                  maxLength={2000}
                  className="form-control"
                  value={declineForm.reason}
                  onChange={(e) => setDeclineForm({ ...declineForm, reason: e.target.value })}
                  placeholder="Briefly explain so support knows what you need..."
                />
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <label>
                  <input
                    type="checkbox"
                    checked={declineForm.confirmed}
                    onChange={(e) => setDeclineForm({ ...declineForm, confirmed: e.target.checked })}
                  />{' '}
                  I understand this declines the invoice and support will be notified.
                </label>
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-8 col-sm-offset-4">
                <button
                  type="button"
                  className="btn btn-warning"
                  disabled={declineBusy}
                  onClick={declineInvoice}>
                  <Icon type={IconType.ban} /> Decline Invoice
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  disabled={declineBusy}
                  onClick={() => setShowDeclineModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = (reduxState) => {
  return reduxState.main;
};

export default connect(mapStateToProps, {})(Invoice);
